<template>
    <div class="flex flex-col gap-4">
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
            <button
                v-for="pt in visibleOptions"
                :key="pt.id"
                type="button"
                class="rounded-xl p-4 text-left transition-opacity shadow-sm"
                :class="[
                    pt.color,
                    itemCurrent === pt.id
                        ? 'opacity-100 ring-2 ring-white ring-offset-2'
                        : 'opacity-60 hover:opacity-90'
                ]"
                @click="selectType(pt.id)"
            >
                <div class="text-2xl mb-1">{{ pt.icon }}</div>
                <div class="font-semibold text-sm text-white leading-tight">
                    {{ pt.label }}
                </div>
                <div class="text-xs text-white opacity-80 mt-1 leading-tight">
                    {{ pt.variants }}
                </div>
            </button>
            <button
                type="button"
                class="rounded-xl p-4 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-theme-border text-theme-secondary hover:border-stack-primary transition-colors"
                @click="openPlanModal"
            >
                <span class="text-2xl">➕</span>
                <span class="font-semibold text-sm">Habilitar más</span>
            </button>
        </div>

        <app-dialog v-model="showPlanModal">
            <template #header>
                <h4 class="text-lg font-semibold text-theme-base">
                    Tipos de producto habilitados
                </h4>
            </template>

            <div class="grid grid-cols-2 md:grid-cols-3 gap-3 py-2">
                <button
                    v-for="pt in allOptions"
                    :key="pt.id"
                    type="button"
                    class="rounded-xl p-3 text-left transition border-2"
                    :class="
                        modalSelectedTypes.includes(pt.id)
                            ? 'border-brand-blue bg-theme-card'
                            : 'border-theme-border bg-theme-card hover:border-theme-primary'
                    "
                    @click="toggleModalType(pt.id)"
                >
                    <div class="text-xl mb-1">{{ pt.icon }}</div>
                    <div class="font-semibold text-xs text-theme-base">
                        {{ pt.label }}
                    </div>
                </button>
            </div>

            <template #footer>
                <div class="flex justify-end gap-2">
                    <app-button flat @click="closePlanModal">
                        Cancelar
                    </app-button>
                    <app-button border @click="useLocallyOnly">
                        Usar solo aquí
                    </app-button>
                    <app-button :loading="isSaving" @click="saveForTenant">
                        Guardar para el tenant
                    </app-button>
                </div>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { ALL_PRODUCT_TYPE_OPTIONS } from "@/common/fields/product-types";

import AppDialog from "@/components/utils/AppDialog.vue";

const CARD_COLORS = [
    "bg-brand-blue",
    "bg-brand-magenta",
    "bg-brand-purple",
    "bg-brand-red",
    "bg-stack-primary",
    "bg-stack-secondary"
];

export default {
    name: "ProductTypePlanManager",
    components: { AppDialog },
    model: {
        prop: "itemCurrent",
        event: "change"
    },
    props: {
        itemCurrent: {
            type: String,
            default: null
        }
    },
    data: () => ({
        tenantAllowedTypes: [],
        localOnlyTypes: [],
        showPlanModal: false,
        modalSelectedTypes: [],
        isSaving: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        allOptions() {
            return ALL_PRODUCT_TYPE_OPTIONS.filter((pt) => pt.id !== "single");
        },
        enabledTypes() {
            return [
                ...new Set([...this.tenantAllowedTypes, ...this.localOnlyTypes])
            ];
        },
        visibleOptions() {
            const filtered = this.enabledTypes.length
                ? this.allOptions.filter((pt) =>
                      this.enabledTypes.includes(pt.id)
                  )
                : this.allOptions;
            return filtered.map((pt, i) => ({
                ...pt,
                color: CARD_COLORS[i % CARD_COLORS.length]
            }));
        }
    },
    async mounted() {
        try {
            const data = await this.getTenantProductTypes();
            if (Array.isArray(data.allowedProductTypes)) {
                this.tenantAllowedTypes = data.allowedProductTypes;
            }
        } catch {
            this.tenantAllowedTypes = [];
        }
    },
    methods: {
        ...mapActions("tenants", [
            "getTenantProductTypes",
            "updateTenantProductTypes"
        ]),
        selectType(id) {
            this.$emit("change", id);
        },
        openPlanModal() {
            this.modalSelectedTypes = [...this.enabledTypes];
            this.showPlanModal = true;
        },
        closePlanModal() {
            this.showPlanModal = false;
        },
        toggleModalType(id) {
            if (this.modalSelectedTypes.includes(id)) {
                this.modalSelectedTypes = this.modalSelectedTypes.filter(
                    (t) => t !== id
                );
            } else {
                this.modalSelectedTypes.push(id);
            }
        },
        useLocallyOnly() {
            this.localOnlyTypes = this.modalSelectedTypes.filter(
                (id) => !this.tenantAllowedTypes.includes(id)
            );
            this.showPlanModal = false;
        },
        async saveForTenant() {
            this.isSaving = true;
            try {
                await this.updateTenantProductTypes({
                    allowedProductTypes: [...this.modalSelectedTypes]
                });
                this.tenantAllowedTypes = [...this.modalSelectedTypes];
                this.localOnlyTypes = [];
                this.showPlanModal = false;
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>
